import { useCallback, useMemo } from 'react';

import { computeCPReturnData } from './useCompanyBillingDetails.utils';

import { OperationVariables } from '@apollo/client';
import { GetCompanyBillingInformationQuery } from '@generated/GetCompanyBillingInformationQuery';
import {
  CompanyDetailsBillingPlanTypes,
  SubscriptionRecurrenceTypes,
} from '@generated/globalTypes';
import { GET_COMPANY_BILLING_DETAILS_QUERY } from '@graphql/queries/GetCompanyDetailsQuery';
import { useGraphQuery } from '@hooks/useQuery';

export type CompanyBillingDetails = {
  paymentMethod: string | null;
  isDelinquent: boolean | null;
  processoutCustomerId: string | null;
  plan: CompanyDetailsBillingPlanTypes | null;
  subscription: {
    billingPeriodStartsAt: string;
    billingPeriodEndsAt: string | null;
    nextInvoiceAt: string;
    recurrence: SubscriptionRecurrenceTypes;
    isAutoRenewal: boolean;
    pricePerAdditionalUser: number | null;
    totalIncludedUsers: number | null;
    maxAdditionalUsers: number | null;
    billingFrequencyAddUserNumber: string | null;
    currency: string | null;
  } | null;
};

export interface UseCompanyBillingDetailsReturn {
  companyBillingDetails: CompanyBillingDetails;
  loading: boolean;
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<CompanyBillingDetails>;
}

/**
 * Hook providing company billing details form CustomerProvisioning
 */
export function useCompanyBillingDetails(): UseCompanyBillingDetailsReturn {
  const {
    data: companyBillingDetails,
    loading,
    refetch,
  } = useGraphQuery<GetCompanyBillingInformationQuery>(GET_COMPANY_BILLING_DETAILS_QUERY);

  const refetchCompanyBillingDetailsFromCP = useCallback(
    async (variables?: Partial<OperationVariables> | undefined) => {
      const { data } = await refetch(variables);

      return computeCPReturnData(data);
    },
    [refetch]
  );

  const useCompanyBillingDetailsReturn: UseCompanyBillingDetailsReturn = useMemo(() => {
    const companyBillingInformation = computeCPReturnData(companyBillingDetails);

    return {
      loading,
      refetch: refetchCompanyBillingDetailsFromCP,
      companyBillingDetails: companyBillingInformation,
    };
  }, [companyBillingDetails, loading, refetchCompanyBillingDetailsFromCP]);

  return useCompanyBillingDetailsReturn;
}
